// src/utils/dataMigration.ts
// VisitHistoryからMedicalRecordへのデータ移行ユーティリティ

import { MedicalRecord, VisitHistory } from '../types/salonboard';
import logger from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';
import { getCustomers, updateCustomer } from './localStorage';

export async function migrateVisitHistoryData(): Promise<void> {
  logger.log('🔄 カルテ・施術履歴データ移行開始...');

  try {
    const customers = getCustomers();
    let migratedCount = 0;
    let errorCount = 0;
    let totalMedicalInfoCount = 0;

    logger.log(`📊 移行対象顧客数: ${customers.length}名`);

    for (const customer of customers) {
      try {
        logger.log(`🔍 顧客処理中: ${customer.name} (ID: ${customer.id})`);
        
        if (!customer.visitHistory || customer.visitHistory.length === 0) {
          logger.log(`  ⏭️  施術履歴なし - スキップ`);
          continue;
        }

        const medicalRecords: MedicalRecord[] = [];
        let customerMigratedCount = 0;

        // 各施術履歴をチェック  
        for (const visit of customer.visitHistory as VisitHistory[]) {
          try {
            // 医療情報が含まれている場合のみ移行
            if (visit.treatmentNotes || visit.products || visit.nextRecommendation) {
              totalMedicalInfoCount++;
              logger.log(`  🔄 施術記録移行中: ${visit.date} (Visit ID: ${visit.id})`);
              
              const medicalRecord: MedicalRecord = {
                id: uuidv4(),
                customerId: customer.id,
                visitHistoryId: visit.id,
                reservationId: visit.reservationId || '',
                date: new Date(visit.date),
                treatmentDetails: {
                  menu: visit.services ? visit.services.join(', ') : '',
                  staff: visit.staff || '',
                  duration: visit.duration || 60,
                  techniques: [], // デフォルト
                  products: visit.products || [],
                },
                physicalCondition: {
                  bloodPressure: '',
                  temperature: 36.5,
                  condition: '良好',
                  concerns: [],
                },
                counseling: {
                  requests: '',
                  suggestions: '',
                  nextRecommendation: visit.nextRecommendation || '',
                },
                conversationNotes: visit.treatmentNotes || '',
                otherNotes: '',
                photos: [],
                createdAt: new Date(visit.date),
                updatedAt: new Date(),
                createdBy: visit.staff || 'システム移行',
                updatedBy: visit.staff || 'システム移行',
              };

              medicalRecords.push(medicalRecord);

              // visitHistoryにmedicalRecordIdを追加（型安全に）
              (visit as unknown as Record<string, unknown>).medicalRecordId = medicalRecord.id;

              // 古いフィールドを削除
              delete visit.treatmentNotes;
              delete visit.products;
              delete visit.nextRecommendation;

              migratedCount++;
              customerMigratedCount++;
              
              logger.log(`  ✅ カルテ作成完了: ${medicalRecord.id}`);
            }
          } catch (visitError) {
            logger.error(`  ❌ 施術記録の移行失敗 (Visit ID: ${visit.id}):`, visitError);
            errorCount++;
          }
        }

        // カスタマーデータを更新
        if (medicalRecords.length > 0) {
          try {
            updateCustomer(customer.id, {
              visitHistory: customer.visitHistory,
              medicalRecords: medicalRecords  // updateCustomer内で既存の配列と連結される
            });
            logger.log(`  ✅ 顧客データ更新完了: ${customerMigratedCount}件のカルテを追加`);
          } catch (updateError) {
            logger.error(`  ❌ 顧客データ更新失敗 (Customer ID: ${customer.id}):`, updateError);
            errorCount++;
          }
        } else {
          logger.log(`  ℹ️  医療情報なし - カルテ作成不要`);
        }
        
      } catch (customerError) {
        logger.error(`❌ 顧客 ${customer.id} の移行失敗:`, customerError);
        errorCount++;
      }
    }

    // 移行結果の詳細な診断（totalMedicalInfoCountは移行処理中に集計済み）

    logger.log(`
📊 データ移行結果:`);
    logger.log(`  ✅ 成功: ${migratedCount}件のカルテを作成`);
    logger.log(`  ❌ エラー: ${errorCount}件`);
    logger.log(`  📋 対象となる医療情報数: ${totalMedicalInfoCount}件`);
    logger.log(`  📈 成功率: ${totalMedicalInfoCount > 0 ? ((migratedCount / totalMedicalInfoCount) * 100).toFixed(1) : 0}%`);

    // データ整合性チェック
    logger.log(`
🔍 データ整合性チェック:`);
    const updatedCustomers = getCustomers();
    const totalCreatedRecords = updatedCustomers.reduce((sum, customer) => 
      sum + (customer.medicalRecords?.length || 0), 0
    );
    logger.log(`  📝 作成されたカルテ総数: ${totalCreatedRecords}件`);
    
    if (totalCreatedRecords !== migratedCount) {
      logger.error(`  ⚠️ 警告: 移行件数(${migratedCount})と実際のカルテ数(${totalCreatedRecords})が一致しません`);
    }
    
    if (errorCount > 0) {
      logger.warn(`⚠️  ${errorCount}件のエラーが発生しました。ログを確認してください。`);
    }
    
  } catch (error) {
    logger.error('❌ データ移行で致命的エラー:', error);
    throw error;
  }
}

// アプリ起動時に一度だけ実行
export function checkAndMigrateData(): void {
  // 統一フラグをチェック - 既に初期化済みの場合はスキップ
  const isInitialized = localStorage.getItem('SALONHUB_COMPLETE_SETUP_v2');
  if (isInitialized === 'true') {
    logger.log('✅ Data migration already completed');
    return;
  }
  
  // レガシーフラグもチェック（互換性維持）
  const legacyMigrationKey = 'visitHistory_to_medicalRecord_migration_v1';
  const isMigrated = localStorage.getItem(legacyMigrationKey);

  if (!isMigrated) {
    migrateVisitHistoryData().then(() => {
      localStorage.setItem(legacyMigrationKey, 'completed');
      logger.log('✅ Legacy data migration completed');
    }).catch(error => {
      logger.error('❌ データ移行中にエラーが発生しました:', error);
    });
  } else {
    logger.log('✅ Legacy data migration already completed');
  }
}